import type TerritoryModel from "@esWebAPI/es/map/TerritoryModel";
import type { ITerritoryActions, ITerritoryException, ITerritoryFeature } from "@esWebAPI/es/map/TerritoryModel";
import type { Feature } from "@esWebAPI/es/model/Feature";
import type {Criteria} from "@esWebAPI/es/model/Criteria";
import type {ILayerView} from "@esWebAPI/es/model/Layer";
import LayerViewModel from "@esWebAPI/es/map/LayerViewModel";

export class TerritoryPointMapping {
    featureViewModel: any;
    territoryModel: TerritoryModel;
    eventChannel: any;

    // Territory Id -> LayerView Id -> Point Id -> Node
    territoryMapping: Map<number, Map<number, Map<number, Node>>> = new Map();

    // LayerMetaId -> Point Id -> Node
    layerPointMapping: Map<number, Map<number, Node>> = new Map();

    // Base Feature Id -> LayerView Id -> Point Id -> Node
    baseFeatureIdMapping: Map<number, Map<number, Map<number, Node>>> = new Map();

    constructor(featureViewModel: unknown, territoryModel: TerritoryModel, eventChannel: unknown) {
        this.featureViewModel = featureViewModel;
        this.territoryModel = territoryModel;
        this.eventChannel = eventChannel;
    }

    init() {
        this.eventChannel.addListener("onTerritoryChange", this.onTerritoryChange, this);
        this.eventChannel.addListener("onRemoveLayer", this.onRemoveLayer, this);
        this.eventChannel.addListener("onUpdateLayer", this.onUpdateLayer, this);
        this.eventChannel.addListener("onAddLayer", this.onAddLayer, this);
        this.eventChannel.addListener("onTerritoryFilterChange", this.onTerritoryFilterChange, this);
    }

    public async loadMapping(): Promise<void> {
        const promises: Promise<void>[] = [];
        // Reset Territory and BaseFeature mappings
        this.territoryMapping = new Map();
        this.baseFeatureIdMapping = new Map();

        for (let i = this.featureViewModel.getNumLayers(); i--;) {
            const lvm = this.featureViewModel.getLayerByIndex(i);
            const layerMetaId = lvm.getLayerMetaId();

            if (!TerritoryPointMapping.isLayerViewSupported(this.territoryModel, lvm.getLayerView())) {
                continue;
            }

            if (this.layerPointMapping.has(layerMetaId)) {
                const linkAttr = this.territoryModel.getLinkAttributeName();
                const mockFeatures = flattenNodes(this.layerPointMapping.get(layerMetaId)!).map(node => ({ id: node.featureId, attrs: { [linkAttr]: node.baseFeatureId } }))
                this.populateMapping(layerMetaId, mockFeatures as Feature[]);
                promises.push(Promise.resolve());
            } else {
                promises.push(this.updateForLayerView(lvm));
            }
        }
        await Promise.allSettled(promises);
    }

    public async updateForLayerView(layerView: LayerViewModel, criteria?: Criteria): Promise<void> {
        criteria = criteria || { $type: "All" };
        const isAccountOnly = this.territoryModel.isAccountOnly();
        const linkAttr = this.territoryModel.isRelatedExtraPointLayer(layerView.getId()) ? this.territoryModel.getExtraPointLinkAttributeName() : this.territoryModel.getLinkAttributeName();
        const features = await es.api.feature.listLean(layerView.getId(), isAccountOnly ? [] : [linkAttr], criteria);
        this.populateMapping(layerView.getLayerMetaId(), features);
        this.eventChannel.fireEvent("onTerritoryPointMappingUpdate");
    }

    private populateMapping(layerMetaId: number, features: Feature[]) {

        features = features ?? [];

        const featureIdPointMapping = new Map<number, Node>();
        const level = this.territoryModel.isHierarchical() ? this.territoryModel.getTerritoryMetaObjRef().levels : null;
        const linkAttr = this.territoryModel.getLinkAttributeName();
        const isAccountOnly = this.territoryModel.isAccountOnly();
        const territoryFilter = this.territoryModel.getFilter();
        const hasTerritoryFilter = territoryFilter !== null;
        const filteredInTerritoryIds = new Set(territoryFilter);
        const featuresFilter = this.territoryModel.getFilteredInFeatureIds(layerMetaId);
        const hasFeatureFilter = featuresFilter != undefined;
        const filteredInFeatureIds = new Set(featuresFilter);

        for (const feature of features) {
            const featureId = feature.id;
            let territoryId = -1;
            let baseFeatureId = undefined;
            let isException = false;
            let isLocked = undefined;
            let terr = this.territoryModel.getByExceptionFeatureIdNoHierarchy(layerMetaId, featureId)
            if (isAccountOnly) {
                if (terr) {
                    territoryId = terr.ES_ID;
                    isLocked = terr.ES_LOCKED_EXCEPTIONS?.some(s => s.layerMetaId == layerMetaId && s.featureIds.includes(featureId)) ?? false;
                }
                isException = true;
            } else {

                baseFeatureId = feature.attrs[linkAttr] as number;

                if (terr) {
                    territoryId = terr.ES_ID;
                    isException = true;
                    isLocked = false;
                } else {
                    terr = this.territoryModel.getByBaseFeatureId(baseFeatureId, level);
                    territoryId = terr?.ES_ID ?? -1;
                    isException = false
                }

            }

            const isPointFilteredOut = (hasFeatureFilter && !filteredInFeatureIds.has(featureId)) || (territoryId !== -1 && hasTerritoryFilter && !filteredInTerritoryIds.has(territoryId));
            const node: Node = {
                featureId,
                layerMetaId,
                isLocked,
                territoryId,
                baseFeatureId,
                isException,
                isVisible: !isPointFilteredOut
            }

            featureIdPointMapping.set(featureId, node);
            this.populateInTerritoryMapping(node);
            this.populateInBaseFeatureMapping(node);
        }
        this.layerPointMapping.set(layerMetaId, featureIdPointMapping);
    }

    private populateInBaseFeatureMapping(node: Node) {

        const { layerMetaId, baseFeatureId, featureId } = node;

        // Update basefeatureIdMapping
        if (baseFeatureId) {

            if (!this.baseFeatureIdMapping.has(baseFeatureId)) {
                this.baseFeatureIdMapping.set(baseFeatureId, new Map());
            }

            if (!this.baseFeatureIdMapping.get(baseFeatureId)!.has(layerMetaId)) {
                this.baseFeatureIdMapping.get(baseFeatureId)!.set(layerMetaId, new Map());

            }

            this.baseFeatureIdMapping.get(baseFeatureId)!.get(layerMetaId)!.set(featureId, node);
        }
    }

    private populateInTerritoryMapping(node: Node) {
        const { territoryId, layerMetaId, featureId } = node;

        if (!this.territoryMapping.has(territoryId)) {
            this.territoryMapping.set(territoryId, new Map());
        }

        if (!this.territoryMapping.get(territoryId)!.has(layerMetaId)) {
            this.territoryMapping.get(territoryId)!.set(layerMetaId, new Map());

        }

        this.territoryMapping.get(territoryId)!.get(layerMetaId)!.set(featureId, node);
    }

    public update() {
        return this.loadMapping()
    }

    public recalculateVisibility(layerMetaId: number, visibleFeatureIds: number[] | undefined, filteredInTerritoryIds: number[] | null) {
        const featureMapping = this.layerPointMapping.get(layerMetaId);
        if (!featureMapping) {
            es.log.warn("Trying to recalculated the visibility of a layer that does not existing in the Territory Mapping", layerMetaId);
            return;
        }

        const hasTerritoryFilter = filteredInTerritoryIds !== null;
        const hasFeatureFilter = visibleFeatureIds != undefined;
        const filteredInFeatureIdsSet = new Set(visibleFeatureIds);
        const filteredInTerritoryIdsSet = new Set(filteredInTerritoryIds);

        for (const node of featureMapping.values()) {
            const isPointFilteredOut = (hasFeatureFilter && !filteredInFeatureIdsSet.has(node.featureId)) || (node.territoryId !== -1 && hasTerritoryFilter && !filteredInTerritoryIdsSet.has(node.territoryId));
            node.isVisible = !isPointFilteredOut
        }
    }


    public clear() {
        this.territoryMapping.clear();
    }

    public async reset(): Promise<void> {
        this.territoryMapping.clear();
        this.baseFeatureIdMapping.clear();
        this.layerPointMapping.clear();
        return this.loadMapping();
    }

    public isFeatVisible(layerMetaId: number, featureId: number) {
        const node = this.layerPointMapping.get(layerMetaId)?.get(featureId);

        if (!node) {
            console.warn("Trying to access a point that doesn't exist", layerMetaId, featureId);
            return true;
        }

        return node.isVisible;
    }

    private onTerritoryChange(actions: ITerritoryActions, territories: ITerritoryFeature[], _changeTerritoryIds: number[]) {

        if (actions["REMOVE_EXCEPTIONS"]) {
            const exceptions = actions["REMOVE_EXCEPTIONS"];
            this.onRemoveExceptions(exceptions);
        }

        if (actions["ADD_EXCEPTIONS"]) {
            const exceptionsPerTerritoryId = actions["ADD_EXCEPTIONS"];
            for (const [territoryId, exceptions] of Object.entries(exceptionsPerTerritoryId)) {
                this.onAddExceptions(exceptions, +territoryId);
            }
        }

        if (actions["ADD_FEATS"] && actions["ADD_FEATS"].baseFeatureIds && actions["ADD_FEATS"].baseFeatureIds.length > 0) {
            this.onAddBaseFeatures(actions["ADD_FEATS"].baseFeatureIds, territories[0]);
        }
        else if (actions["REMOVE_FEATS"] && actions["REMOVE_FEATS"].baseFeatureIds && actions["REMOVE_FEATS"].baseFeatureIds.length > 0) {
            this.onRemoveBaseFeatures(actions["REMOVE_FEATS"].baseFeatureIds);
        }

        if (actions["RELOAD"] || actions["UNDO"] || actions["REDO"]) {
            this.update();
        }
        else if (actions["REMOVE_TERRITORY"]) {
            this.onRemoveTerritory(territories);
        }
        else if (actions["LOCK_EXCEPTIONS"]) {
            const { layerMetaId, featureIds } = actions["LOCK_EXCEPTIONS"];
            this.setLockedExceptions(layerMetaId, featureIds, true);
        }
        else if (actions["UNLOCK_EXCEPTIONS"]) {
            const { layerMetaId, featureIds } = actions["UNLOCK_EXCEPTIONS"];
            this.setLockedExceptions(layerMetaId, featureIds, false);
        }
    }


    private onRemoveTerritory(territories: ITerritoryFeature[]) {
        const levels = this.territoryModel.getTerritoryMetaObjRef().levels;

        for (const territory of territories ?? []) {
            if (territory.ES_LEVEL !== levels) {
                continue;
            }

            if (!this.territoryMapping.has(territory.ES_ID)) {
                continue;
            }

            const affectedNodes = flattenNodes(this.territoryMapping.get(territory.ES_ID)!);

            for (const node of affectedNodes) {
                let targetTerritoryId = -1;
                if (node.baseFeatureId !== undefined) {
                    targetTerritoryId = this.territoryModel.getByBaseFeatureId(node.baseFeatureId, null)?.ES_ID ?? -1;
                }
                node.territoryId = targetTerritoryId;
                node.isException = false;
                node.isLocked = false;
                this.populateInTerritoryMapping(node);
            }

            this.territoryMapping.delete(territory.ES_ID);
        }
    }


    private onRemoveExceptions(exceptions: ITerritoryException[]) {

        const isAccountOnly = this.territoryModel.isAccountOnly();
        for (const exception of exceptions) {

            const layerMetaId = exception.layerMetaId;

            for (const featureId of exception.featureIds) {

                const node = this.layerPointMapping.get(layerMetaId)!.get(featureId) as Node;
                this.territoryMapping.get(node.territoryId)!.get(node.layerMetaId)!.delete(node.featureId);

                let terr = -1;

                if (!isAccountOnly) {
                    terr = this.territoryModel.getByBaseFeatureId(node.baseFeatureId, null)?.ES_ID ?? -1;
                }

                node.territoryId = terr;
                node.isLocked = false;
                node.isException = false;
                this.populateInTerritoryMapping(node);
            }
        }
    }
    private onAddExceptions(exceptions: ITerritoryException[], territoryId: number) {

        for (let e = 0; e < exceptions.length; e++) {
            const exception = exceptions[e];
            const layerMetaId = exception.layerMetaId;
            const featureIds = exception.featureIds;

            for (const featureId of featureIds) {

                const node = this.layerPointMapping.get(layerMetaId)!.get(featureId) as Node;
                this.territoryMapping.get(node.territoryId)!.get(layerMetaId)!.delete(node.featureId);
                node.isException = true;
                node.isLocked = false;
                node.territoryId = territoryId;
                this.populateInTerritoryMapping(node);
            }
        }
    }


    private onAddBaseFeatures(baseFeatureIds: number[], territory: ITerritoryFeature) {
        for (const baseFeatureId of baseFeatureIds) {

            for(const node of this.getBaseFeatureIdIterator(baseFeatureId)) {
                if (node.isException) {
                    continue;
                }

                // Remove Link from Territory Mapping
                this.territoryMapping.get(node.territoryId)!.get(node.layerMetaId)!.delete(node.featureId);

                node.isLocked = false;
                node.isException = false;
                node.territoryId = territory.ES_ID;
                this.populateInTerritoryMapping(node);
            }
        }
    }

    private onRemoveBaseFeatures(baseFeatureIds: number[]) {
        for (const baseFeatureId of baseFeatureIds) {
            for (const node of this.getBaseFeatureIdIterator(baseFeatureId)) {

                if (node.isException) {
                    continue;
                }

                // Remove Link from Territory Mapping
                this.territoryMapping.get(node.territoryId)!.get(node.layerMetaId)!.delete(node.featureId);

                node.isLocked = false;
                node.isException = false;
                node.territoryId = -1;
                this.populateInTerritoryMapping(node);
            }
        }
    }

    private setLockedExceptions(layerMetaId: number, featureIds: number[], isLocked: boolean) {
        for (const featureId of featureIds) {
            const node = this.layerPointMapping.get(layerMetaId).get(featureId) as Node;
            node.isLocked = isLocked;
        }
    }

    private onRemoveLayer(layerView: any) {
        const layerMetaId = layerView.getLayerMetaId();

        this.layerPointMapping.delete(layerMetaId);

        for (const [key, _value] of this.territoryMapping) {
            this.territoryMapping.get(key)?.delete(layerMetaId);
        }

        for (const [key, _value] of this.baseFeatureIdMapping) {
            this.territoryMapping.get(key)?.delete(layerMetaId);
        }
    }

    private onAddLayer(layerViewModel: any) {
        if (!TerritoryPointMapping.isLayerViewSupported(this.territoryModel, layerViewModel.getLayerView())) {
            return;
        }

        this.updateForLayerView(layerViewModel);
    }

    private onTerritoryFilterChange(_action = "CHANGE_FILTER", _filteredInTerritoryIds: number[]) {
        for (const layerMetaId of this.layerPointMapping.keys()) {
            const filteredInFeatureIds = this.territoryModel.getFilteredInFeatureIds(layerMetaId);
            this.recalculateVisibility(layerMetaId, filteredInFeatureIds, this.territoryModel.getFilter());
        }
    }

    public getNode(layerMetaId: number, featureId: number) {
        return this.layerPointMapping.get(layerMetaId)?.get(featureId) ?? null;
    }

    public getBaseFeatureIdIterator(baseFeatureId: number) {
        const featuresByLayerMetaId = this.baseFeatureIdMapping.get(baseFeatureId);
        if (!featuresByLayerMetaId) {
            const emptyIterator = {}
            emptyIterator[Symbol.iterator] = function() {
                return {
                    next: () => ({ done: true })
                }
            }

            return emptyIterator;
        }


        const iterator: { layerMetaIdIterator: Iterator<number, any, undefined>, nodeIterator: Iterator<Node> } = {
            layerMetaIdIterator: featuresByLayerMetaId.keys(),
            nodeIterator: null as unknown as Iterator<Node>
        }

        iterator[Symbol.iterator] = function() {

            return {

                next() {
                    if (!iterator.nodeIterator) {
                        iterator.nodeIterator = featuresByLayerMetaId.get(iterator.layerMetaIdIterator.next().value)!.values();
                    }

                    let iteratorResult = iterator.nodeIterator.next();
                    if (iteratorResult.done) {
                        const nextLayerMetaId = iterator.layerMetaIdIterator.next().value;
                        if (!nextLayerMetaId) {
                            return { done: true }
                        }
                        iterator.nodeIterator = featuresByLayerMetaId.get(nextLayerMetaId)!.values();
                        iteratorResult = iterator.nodeIterator.next();
                    }
                    return iteratorResult;
                }
            }
        }
        return iterator;
    }

    private onUpdateLayer(layerViewModel: any) {

        if (!TerritoryPointMapping.isLayerViewSupported(this.territoryModel, layerViewModel.getLayerView())) {
            return;
        }

        this.layerPointMapping.delete(layerViewModel.getLayerMetaId());
        this.updateForLayerView(layerViewModel);
    }

    public static isLayerViewSupported(_territoryModel: TerritoryModel, layerView: ILayerView){
        return layerView.geomType === "POINT" &&
            layerView.$type === "DefaultLayerView" /*&&
            !territoryModel.isRelatedExtraPointLayer(layerView.id)*/;
    }
}

function flattenNodes(mapping: Map<number, Map<number, Node>> | Map<number, Map<number, Map<number, Node>>> | Map<number, Node>): Node[] {
    const returnNodes: Node[] = []


    if(!mapping) {
        return [];
    }

    for (const nodeOrMap of mapping.values()) {

        if (nodeOrMap instanceof Map) {

            for (const nodeOrMap2 of nodeOrMap.values()) {
                if (nodeOrMap2 instanceof Map) {
                    returnNodes.push(...nodeOrMap2.values())
                } else {
                    returnNodes.push(nodeOrMap2)
                }
            }
        } else {
            returnNodes.push(nodeOrMap)
        }
    }
    return returnNodes;
}

type Node = {
    territoryId: number
    isLocked?: boolean
    isException: boolean
    baseFeatureId?: number
    layerMetaId: number
    featureId: number
    isVisible: boolean
}
